/**
 * =============================================================================
 * LAUNCH UNAVAILABLE PANEL (Phase 4.5 controlled-launch slice)
 * =============================================================================
 *
 * Presentational panel rendered by the widget when the tenant's public launch
 * mode is not VISIBLE (HIDDEN / COMING_SOON), or when availability could not be
 * resolved (fail-closed). Replaces the entire normal widget surface — no form,
 * no navigation, no browse — so a half-finished public surface is not exposed.
 *
 * Stateless and side-effect free; the gating decision lives in the widget
 * bootstrap (`AvailabilityGate` in `main.tsx`).
 * =============================================================================
 */

import React from 'react';

/** Fallback copy used when no server-provided message is available. */
export const DEFAULT_UNAVAILABLE_MESSAGE = 'Find My Part is not currently available.';

export interface LaunchUnavailableProps {
  /**
   * Customer-facing message to display. When `null`/`undefined` (e.g. the
   * availability request failed and we fail closed), a neutral default is used.
   */
  message?: string | null;
}

/**
 * Renders a calm, neutral "not available / coming soon" panel.
 */
export function LaunchUnavailable({ message }: LaunchUnavailableProps): React.ReactElement {
  const text = message && message.trim().length > 0 ? message : DEFAULT_UNAVAILABLE_MESSAGE;

  return (
    <div className="fmp-widget fmp-unavailable" data-testid="fmp-launch-unavailable">
      <div className="fmp-unavailable__card" role="status" aria-live="polite">
        <h2 className="fmp-unavailable__title">Find My Part</h2>
        <p className="fmp-unavailable__message">{text}</p>
      </div>
      <style>{unavailableStyles}</style>
    </div>
  );
}

const unavailableStyles = `
  :where(.fmp-widget-scope) .fmp-unavailable {
    max-width: var(--fmp-layout-widget-max);
    margin: 0 auto;
    font-family: var(--fmp-font-family);
  }
  :where(.fmp-widget-scope) .fmp-unavailable__card {
    padding: var(--fmp-size-40) var(--fmp-space-2xl);
    text-align: center;
    border: var(--fmp-border-width) solid var(--fmp-color-border);
    border-radius: var(--fmp-radius-control);
    background-color: var(--fmp-color-surface-muted);
    margin: var(--fmp-space-2xl) auto;
    max-width: var(--fmp-size-480);
  }
  :where(.fmp-widget-scope) .fmp-unavailable__title {
    margin: 0 0 var(--fmp-space-md);
    font-size: var(--fmp-font-size-lg);
    font-weight: var(--fmp-font-weight-semibold);
    color: var(--fmp-color-text);
  }
  :where(.fmp-widget-scope) .fmp-unavailable__message {
    margin: 0;
    font-size: var(--fmp-font-size-base);
    line-height: var(--fmp-line-height-body);
    color: var(--fmp-color-text-hint);
  }
`;
